// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The 2D playback panel's date track - the same strip the headset draws,
 * in the transport where a mouse and a thumb already are.
 *
 * It exists because a real-time stream is a time series pretending to be
 * a video: the scrubber moves through two minutes of encoding that
 * represent thirty days of data, and until now nothing on that panel said
 * which instant the sphere was showing. The axis comes from the stream's
 * own `.dsa` (`services/dsaTimeline.ts`), the drawing from the shared
 * renderer (`services/timelineTrackCanvas.ts`), and this module is only
 * the DOM half: a canvas sized to its container, pointer and keyboard
 * input, and the ARIA slider semantics the scrubber next to it already
 * has.
 *
 * Three decisions:
 *
 *   - **Rationed seeks.** A pointer drag fires dozens of move events a
 *     second and each seek stalls the decoder. The drawn playhead follows
 *     the pointer at once, while the video is steered at most every
 *     `SCRUB_SEEK_INTERVAL_MS` - the same ration the headset uses, from one
 *     constant, because it is the same decoder problem.
 *   - **Compact drawing.** The panel is a few hundred pixels wide, so the
 *     renderer drops the header row and lets this strip's own ends carry
 *     the range; the current instant is already on screen in the panel's
 *     time label, which now fills in for these streams too.
 *   - **Keyboard parity.** The strip is a slider to a screen reader and
 *     to the tab key: arrows step one frame of the axis, Home and End jump
 *     to its ends, and `aria-valuetext` carries the instant rather than a
 *     percentage, because the instant is the thing being chosen.
 *   - **AYNI: the date under the pointer.** A mouse resting on the strip
 *     shows, above it, the date a click there would go to (snapped to the
 *     frame a seek lands on) and why that frame is not a measurement, if
 *     it is not; a finger, which cannot hover, sees it while dragging. It
 *     is DOM over the canvas, not part of the drawing, so moving the mouse
 *     never repaints the strip, and it is hidden from assistive tech,
 *     which already hears the instant in `aria-valuetext`.
 *   - **AYNI: shortcuts on an overlay's caption.** An overlay's track
 *     carries, at the right of its caption, an eye that hides the overlay
 *     on the globe (it keeps playing in step, so showing it again is
 *     instant) and a cross that takes it off. They sit in the caption row,
 *     outside the slider, because a slider's children are not buttons to a
 *     screen reader and a press on them must not seek.
 *   - **AYNI: the order and the colours (owner, 2026-10-09).** Left of the
 *     strip, a two-part control as tall as the strip: an overlay's grip
 *     (hold and drag the row up or down to change the order the overlays
 *     are drawn in; `overlayStackDrag.ts`) and its palette button (the
 *     palette and min/max dialog, `overlayPaletteDialog.ts`). The dataset's
 *     track has a lock in the grip's place, since nothing goes below it.
 *     Outside the slider, for the same reason as the caption's buttons.
 *
 * See {@link file://./../../docs/VR_PLAYBACK_TRACK_PLAN.md VR_PLAYBACK_TRACK_PLAN.md}.
 */

import { t } from '../i18n'
import { paletteButtonLabel } from './overlayPaletteDialog'
import {
  SCRUB_SEEK_INTERVAL_MS,
  drawTimelineTrack,
  formatPlayheadLabel,
  hoverReadoutAt,
  progressAtCanvasX,
  progressToCanvasX,
  stepFrameMs,
  timelineTrackGeometry,
  type TimelineTrackState,
} from '../services/timelineTrackCanvas'

/** CSS height of the strip. The renderer is proportional, so this is a choice. */
const TRACK_HEIGHT_CSS = 56

/** AYNI: the caption's shortcut icons (stroked, sized by the stylesheet). */
const ICON_ATTRS = 'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"'
const ICON_EYE = `<svg ${ICON_ATTRS}><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg>`
const ICON_EYE_OFF = `<svg ${ICON_ATTRS}><path d="M9.9 5.2A9.7 9.7 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-2.6 3.5M6.6 6.6C3.9 8.4 2 12 2 12s3.6 7 10 7a9.5 9.5 0 0 0 5.4-1.6"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M2 2l20 20"/></svg>`
const ICON_CLOSE = `<svg ${ICON_ATTRS}><path d="M18 6 6 18M6 6l12 12"/></svg>`
/** AYNI: the grip (two columns of dots) and the lock on the dataset's track. */
const ICON_GRIP = '<svg viewBox="0 0 8 16" fill="currentColor" aria-hidden="true" focusable="false"><circle cx="2" cy="2.5" r="1.4"/><circle cx="6" cy="2.5" r="1.4"/><circle cx="2" cy="8" r="1.4"/><circle cx="6" cy="8" r="1.4"/><circle cx="2" cy="13.5" r="1.4"/><circle cx="6" cy="13.5" r="1.4"/></svg>'
const ICON_LOCK = `<svg ${ICON_ATTRS}><rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 0 1 8 0v4"/></svg>`

/** ARIA slider granularity — the strip reports progress in thousandths,
 *  matching the scrubber's own range. */
const ARIA_MAX = 1000

export interface TimelineTrackUIOptions {
  /**
   * The user picked an instant, or dragged to one. Called at most once
   * per {@link SCRUB_SEEK_INTERVAL_MS} while dragging, and once more on
   * release, so a caller may simply seek.
   */
  readonly onSeek: (epochMs: number) => void
  /** AYNI: the strip's height in CSS px (the overlay strip is a little shorter). */
  readonly height?: number
  /** AYNI: its accessible name, when it is not the dataset's date track. */
  readonly ariaLabel?: string
  /** AYNI: an eye at the right of the caption; called with the state asked for (the dataset's track too, since 2026-10-12). */
  readonly onHiddenChange?: (hidden: boolean) => void
  /** AYNI: a cross at the right of the caption (on the dataset's track it takes the dataset and its overlays off). */
  readonly onClose?: () => void
  /**
   * AYNI: the two-part control left of the strip. An overlay's has a
   * grip (the caller wires the drag to `grip`) and the dataset's a lock;
   * both have the palette button, which calls `onPalette`.
   */
  readonly stack?: {
    readonly kind: 'overlay' | 'base'
    readonly onPalette: (button: HTMLButtonElement) => void
  }
}

/** AYNI: what the control left of the strip shows (null hides it). */
export interface TimelineTrackStackState {
  /** The palette's ramp as a CSS gradient, or null when the layer's colours cannot change. */
  readonly swatch: string | null
  /** Why they cannot, said by the greyed button. */
  readonly reason?: string
  /** The grip is greyed (a single overlay has nowhere to go). */
  readonly gripDisabled?: boolean
}

export interface TimelineTrackUIHandle {
  /** The element to insert into the transport panel (the caption row and the strip). */
  readonly element: HTMLElement
  /** The strip itself: the focusable slider. */
  readonly slider: HTMLElement
  /** Draw a new axis, or hide the strip with `null`. Idempotent. */
  setState(state: TimelineTrackState | null): void
  /** AYNI: a short line above the strip naming what it shows (null: none). */
  setCaption(text: string | null): void
  /** AYNI: what the eye shows (the overlay hidden on the globe, or not). */
  setHidden(hidden: boolean): void
  /** AYNI: the grip, for the caller to wire the drag to (null on the dataset's track). */
  readonly grip: HTMLButtonElement | null
  /** AYNI: the palette button (null without a stack control). */
  readonly paletteButton: HTMLButtonElement | null
  /** AYNI: show the control left of the strip, or hide it with null. */
  setStack(state: TimelineTrackStackState | null): void
  isVisible(): boolean
  dispose(): void
}

export function createTimelineTrackUI(
  opts: TimelineTrackUIOptions,
): TimelineTrackUIHandle {
  const element = document.createElement('div')
  element.className = 'timeline-track hidden'
  const slider = document.createElement('div')
  slider.className = 'timeline-track-strip'
  slider.tabIndex = 0
  slider.setAttribute('role', 'slider')
  slider.setAttribute('aria-label', opts.ariaLabel ?? t('timeline.aria'))
  slider.setAttribute('aria-valuemin', '0')
  slider.setAttribute('aria-valuemax', String(ARIA_MAX))

  const trackHeight = opts.height ?? TRACK_HEIGHT_CSS
  const caption = document.createElement('div')
  caption.className = 'timeline-track-caption'
  caption.hidden = true
  const captionText = document.createElement('span')
  captionText.className = 'timeline-track-caption-text'
  caption.appendChild(captionText)
  // AYNI: the shortcuts (an overlay's, and the dataset's since 2026-10-12), named after the caption once it is known.
  const base = opts.stack?.kind === 'base'
  let hidden = false
  const eye = opts.onHiddenChange ? captionButton('eye', () => opts.onHiddenChange?.(!hidden)) : null
  const close = opts.onClose ? captionButton('close', () => opts.onClose?.()) : null
  if (eye || close) {
    const actions = document.createElement('span')
    actions.className = 'timeline-track-actions'
    if (eye) actions.appendChild(eye)
    if (close) actions.appendChild(close)
    caption.appendChild(actions)
  }
  // AYNI: the control left of the strip, on one line with it.
  const line = document.createElement('div')
  line.className = 'timeline-track-line'
  let stackBox: HTMLElement | null = null
  let grip: HTMLButtonElement | null = null
  let paletteButton: HTMLButtonElement | null = null
  let paletteSwatch: HTMLElement | null = null
  let lock: HTMLElement | null = null
  let stackState: TimelineTrackStackState | null = null
  if (opts.stack) {
    const stack = opts.stack
    stackBox = document.createElement('div')
    stackBox.className = 'timeline-track-stack'
    stackBox.dataset.kind = stack.kind
    stackBox.hidden = true
    stackBox.style.blockSize = trackHeight + 'px'
    if (stack.kind === 'overlay') {
      grip = document.createElement('button')
      grip.type = 'button'
      grip.className = 'timeline-track-grip'
      grip.innerHTML = ICON_GRIP
      stackBox.appendChild(grip)
    } else {
      lock = document.createElement('span')
      lock.className = 'timeline-track-lock'
      lock.setAttribute('role', 'img')
      lock.innerHTML = ICON_LOCK
      stackBox.appendChild(lock)
    }
    paletteButton = document.createElement('button')
    paletteButton.type = 'button'
    paletteButton.className = 'timeline-track-palette'
    paletteButton.setAttribute('aria-haspopup', 'dialog')
    paletteButton.setAttribute('aria-expanded', 'false')
    paletteSwatch = document.createElement('span')
    paletteSwatch.className = 'timeline-track-palette-ramp'
    paletteSwatch.setAttribute('aria-hidden', 'true')
    paletteButton.appendChild(paletteSwatch)
    const button = paletteButton
    // AYNI (owner, 2026-10-12): it opens for every layer, for the transparency at least.
    paletteButton.addEventListener('click', () => {
      if (stackState) stack.onPalette(button)
    })
    stackBox.appendChild(paletteButton)
    line.appendChild(stackBox)
  }
  line.appendChild(slider)
  element.append(caption, line)
  const canvas = document.createElement('canvas')
  slider.appendChild(canvas)
  // AYNI: the date under the pointer, and a hairline on the bar where it is.
  const hover = document.createElement('div')
  hover.className = 'timeline-track-hover'
  hover.hidden = true
  hover.setAttribute('aria-hidden', 'true')
  const guide = document.createElement('div')
  guide.className = 'timeline-track-hover-guide'
  guide.hidden = true
  guide.setAttribute('aria-hidden', 'true')
  element.append(hover, guide)

  let ctx: CanvasRenderingContext2D | null = null
  let state: TimelineTrackState | null = null
  /** Progress the pointer is holding, or null when no drag is in flight. */
  let dragProgress: number | null = null
  /** AYNI: progress the hover readout names, or null while it is hidden. */
  let hoverProgress: number | null = null
  let lastSeekAt = 0
  let disposed = false
  /** What is on the canvas, so a per-frame poll does not repaint for an
   *  unchanged frame. */
  let drawn = ''

  /** Canvas pixels per CSS pixel, so the strip is crisp on a retina panel. */
  function deviceScale(): number {
    return typeof window !== 'undefined' && window.devicePixelRatio > 0
      ? window.devicePixelRatio
      : 1
  }

  /** Size the canvas to its container and return the geometry to draw in.
   *  Geometry is in CSS pixels; the context is scaled, so the renderer's
   *  fractions stay readable numbers. */
  /** The strip's width: the line's, less the control beside it. */
  function stripWidth(): number {
    return Math.max(1, Math.round(slider.clientWidth || element.clientWidth || 240))
  }

  function fit(): ReturnType<typeof timelineTrackGeometry> | null {
    const width = stripWidth()
    const height = trackHeight
    const scale = deviceScale()
    const next = timelineTrackGeometry(width, height)
    const pixelWidth = Math.round(width * scale)
    const pixelHeight = Math.round(height * scale)
    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth
      canvas.height = pixelHeight
    }
    canvas.style.height = height + 'px'
    const context = canvas.getContext('2d')
    if (!context) return null
    // Reset before scaling: assigning width above already cleared it, but a
    // fresh transform is what keeps repeated fits from compounding. Guarded
    // because a test context stub need not implement it.
    if (typeof context.setTransform === 'function') {
      context.setTransform(scale, 0, 0, scale, 0, 0)
    }
    ctx = context
    return next
  }

  /** The state as drawn: a drag overrides the playhead so it follows the
   *  pointer even between rationed seeks. */
  function drawnState(): TimelineTrackState | null {
    if (!state) return null
    if (dragProgress === null) return state
    return {
      ...state,
      currentMs: state.startMs + dragProgress * (state.endMs - state.startMs),
      scrubbing: true,
    }
  }

  function signature(next: TimelineTrackState): string {
    const spans = next.availabilitySpans
    return [
      next.startMs,
      next.endMs,
      next.currentMs,
      next.frameCount,
      next.cadenceMs,
      next.scrubbing ? 1 : 0,
      spans.length,
      spans[0]?.startFrame ?? -1,
      spans[spans.length - 1]?.startFrame ?? -1,
      next.accent ?? 'base',
      next.coverage ? next.coverage.startMs + '-' + next.coverage.endMs : '',
      stripWidth(),
    ].join('|')
  }

  function paint(): void {
    const next = drawnState()
    if (!next) {
      element.classList.add('hidden')
      slider.removeAttribute('aria-valuenow')
      slider.removeAttribute('aria-valuetext')
      drawn = ''
      hideHover()
      return
    }
    element.classList.remove('hidden')
    // A new axis under a resting pointer names its own date there.
    if (hoverProgress !== null) setHoverText(next, hoverProgress)
    const geometry = fit()
    const key = signature(next)
    if (!ctx || !geometry || key === drawn) {
      if (geometry) updateAria(next)
      return
    }
    drawn = key
    drawTimelineTrack(ctx, next, geometry)
    updateAria(next)
  }

  function updateAria(next: TimelineTrackState): void {
    const span = next.endMs - next.startMs
    const progress = span > 0 ? Math.max(0, Math.min(1, (next.currentMs - next.startMs) / span)) : 0
    slider.setAttribute('aria-valuenow', String(Math.round(progress * ARIA_MAX)))
    slider.setAttribute('aria-valuetext', formatPlayheadLabel(next.currentMs, next.cadenceMs))
  }

  /** Geometry of the strip as laid out now, in CSS pixels. */
  function layoutGeometry(): ReturnType<typeof timelineTrackGeometry> {
    return timelineTrackGeometry(stripWidth(), trackHeight)
  }

  /** Axis progress at a client x, or null when the pointer is off the bar.
   *  The canvas is laid out at the element's width, so a client x maps to
   *  canvas x by the rect alone — no device-pixel factor here, because the
   *  geometry is in CSS pixels. */
  function progressAtClientX(clientX: number): number | null {
    const rect = canvas.getBoundingClientRect()
    return progressAtCanvasX(clientX - rect.left, layoutGeometry())
  }

  /** AYNI: the readout's words for a progress on this axis. */
  function setHoverText(current: TimelineTrackState, progress: number): void {
    const { ms, note, bands } = hoverReadoutAt(current, progress)
    const date = formatPlayheadLabel(ms, current.cadenceMs)
    let text = date
    if (note === 'uncovered') text = t('timeline.hover.uncovered', { date })
    else if (note === 'missing') text = t('timeline.hover.missing', { date })
    else if (note === 'filled') text = t('timeline.hover.filled', { date })
    else if (note === 'estimated') text = t('timeline.hover.estimated', { date })
    // AYNI: which satellite supplied this period (satellites ↔ catalog plan, step 2).
    if (bands.length) {
      const names = bands.map((b) => (b.approximate ? t('timeline.hover.bandAbout', { name: b.label }) : b.label)).join(', ')
      text = t('timeline.hover.band', { text, names })
    }
    // Called every frame while a pointer rests here: only a change touches the DOM.
    if (hover.textContent !== text) hover.textContent = text
  }

  /** AYNI: show the readout for a progress, over that point of the bar. */
  function showHover(progress: number): void {
    if (!state) return
    hoverProgress = progress
    setHoverText(state, progress)
    hover.hidden = false
    guide.hidden = false
    // Physical pixels, not logical: the canvas draws time left to right in
    // every locale, so the readout follows the drawing, not the text.
    const geometry = layoutGeometry()
    const x = canvas.offsetLeft + progressToCanvasX(progress, geometry)
    guide.style.left = x + 'px'
    guide.style.top = canvas.offsetTop + geometry.bar.y + 'px'
    guide.style.height = geometry.bar.h + 'px'
    // Centred on the point, but kept inside the strip at either end
    // (measured unrounded, so the right end does not overhang by a fraction).
    const bubbleWidth = hover.getBoundingClientRect().width
    const maxLeft = Math.max(0, element.clientWidth - bubbleWidth)
    hover.style.left = Math.max(0, Math.min(maxLeft, x - bubbleWidth / 2)) + 'px'
  }

  function hideHover(): void {
    if (hoverProgress === null && hover.hidden) return
    hoverProgress = null
    hover.hidden = true
    guide.hidden = true
  }

  function seekFromPointer(clientX: number, commit: boolean): void {
    const current = drawnState()
    if (!current) return
    const progress = progressAtClientX(clientX)
    if (progress === null) return
    dragProgress = progress
    const now = performance.now()
    if (commit || now - lastSeekAt >= SCRUB_SEEK_INTERVAL_MS) {
      lastSeekAt = now
      opts.onSeek(current.startMs + progress * (current.endMs - current.startMs))
    }
    paint()
    showHover(progress)
  }

  function onPointerDown(ev: PointerEvent): void {
    if (!state) return
    ev.preventDefault()
    // A fingertip covers what is just above it: the stylesheet lifts the
    // readout higher for touch.
    hover.dataset.pointer = ev.pointerType || 'mouse'
    slider.focus()
    try {
      slider.setPointerCapture(ev.pointerId)
    } catch {
      // Capture is a nicety; the drag still works without it.
    }
    seekFromPointer(ev.clientX, true)
  }

  function onPointerMove(ev: PointerEvent): void {
    if (!state) return
    if (dragProgress !== null) {
      seekFromPointer(ev.clientX, false)
      return
    }
    // AYNI: a mouse or pen resting on the strip; a finger only moves it
    // while pressed, which is the drag above.
    if (ev.pointerType === 'touch') return
    hover.dataset.pointer = ev.pointerType || 'mouse'
    const progress = progressAtClientX(ev.clientX)
    if (progress === null) hideHover()
    else showHover(progress)
  }

  function onPointerUp(ev: PointerEvent): void {
    if (dragProgress === null) return
    seekFromPointer(ev.clientX, true)
    dragProgress = null
    paint()
    // A lifted finger has nothing left to point at; a mouse still does.
    if (ev.pointerType === 'touch') hideHover()
  }

  function onPointerLeave(): void {
    if (dragProgress === null) hideHover()
  }

  function onKeyDown(ev: KeyboardEvent): void {
    const current = drawnState()
    if (!current) return
    // AYNI: one frame at a time on the frame calendar (a calendar month on
    // a monthly axis), not the cadence added in milliseconds.
    let target: number | null = null
    if (ev.key === 'ArrowLeft') target = stepFrameMs(current, current.currentMs, -1)
    else if (ev.key === 'ArrowRight') target = stepFrameMs(current, current.currentMs, 1)
    else if (ev.key === 'Home') target = current.startMs
    else if (ev.key === 'End') target = stepFrameMs(current, current.endMs, current.frameCount)
    if (target === null) return
    ev.preventDefault()
    dragProgress = null
    opts.onSeek(target)
  }

  const onResize = (): void => {
    hideHover()
    if (state) paint()
  }

  slider.addEventListener('pointerdown', onPointerDown)
  slider.addEventListener('pointermove', onPointerMove)
  slider.addEventListener('pointerup', onPointerUp)
  slider.addEventListener('pointercancel', onPointerUp)
  slider.addEventListener('pointerleave', onPointerLeave)
  slider.addEventListener('keydown', onKeyDown)
  window.addEventListener('resize', onResize)

  /** AYNI: one of the caption's shortcut buttons. */
  function captionButton(kind: 'eye' | 'close', onClick: () => void): HTMLButtonElement {
    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'timeline-track-action'
    button.dataset.action = kind
    button.innerHTML = kind === 'eye' ? ICON_EYE : ICON_CLOSE
    button.addEventListener('click', onClick)
    return button
  }

  /** AYNI: the shortcuts' names and the eye's look, for this caption and state. */
  function labelActions(): void {
    const title = captionText.textContent ?? ''
    if (eye) {
      const label = base
        ? t(hidden ? 'timeline.base.show' : 'timeline.base.hide', { title })
        : t(hidden ? 'timeline.overlay.show' : 'timeline.overlay.hide', { title })
      eye.setAttribute('aria-label', label)
      eye.title = label
      eye.setAttribute('aria-pressed', String(hidden))
      eye.innerHTML = hidden ? ICON_EYE_OFF : ICON_EYE
    }
    if (close) {
      const label = t(base ? 'timeline.base.close' : 'timeline.overlay.close', { title })
      close.setAttribute('aria-label', label)
      close.title = label
    }
    labelStack()
  }

  /** AYNI: the control's names and looks, for this caption and state. */
  function labelStack(): void {
    if (!stackBox) return
    const title = captionText.textContent ?? ''
    if (grip) {
      const label = t('timeline.overlay.move', { title })
      grip.setAttribute('aria-label', label)
      grip.title = label
      grip.disabled = !!stackState?.gripDisabled
    }
    if (lock) {
      const label = t('timeline.base.lock')
      lock.setAttribute('aria-label', label)
      lock.title = label
    }
    if (paletteButton && paletteSwatch) {
      const swatch = stackState?.swatch ?? null
      // Colours that cannot change are hatched; the button still opens the
      // dialog, which says why and holds the layer's transparency.
      const label = swatch ? paletteButtonLabel(title) : t('palette.dialog.openFixed', { title })
      paletteButton.setAttribute('aria-label', label)
      paletteButton.title = swatch || !stackState?.reason ? label : `${stackState.reason}. ${label}`
      paletteButton.toggleAttribute('data-fixed', !swatch)
      paletteSwatch.style.background = swatch ?? ''
    }
  }

  return {
    element,
    slider,
    setState(next) {
      if (disposed) return
      state = next
      if (!next) dragProgress = null
      paint()
    },
    setCaption(text) {
      // Called every frame: only a change touches the DOM.
      if ((text ?? '') === captionText.textContent && caption.hidden === !text) return
      captionText.textContent = text ?? ''
      caption.hidden = !text
      labelActions()
    },
    setHidden(next) {
      // Called every frame: only a change touches the DOM.
      if (next === hidden) return
      hidden = next
      element.classList.toggle('overlay-hidden', hidden)
      labelActions()
    },
    grip,
    paletteButton,
    setStack(next) {
      if (!stackBox) return
      // Called every frame: only a change touches the DOM.
      const same = next === stackState || (!!next && !!stackState && next.swatch === stackState.swatch && next.reason === stackState.reason && next.gripDisabled === stackState.gripDisabled)
      if (same) return
      const shownChanged = !next !== !stackState
      stackState = next
      stackBox.hidden = !next
      labelStack()
      // The strip got narrower or wider: draw it at its new width.
      if (shownChanged && state) paint()
    },
    isVisible() {
      return state !== null
    },
    dispose() {
      if (disposed) return
      disposed = true
      slider.removeEventListener('pointerdown', onPointerDown)
      slider.removeEventListener('pointermove', onPointerMove)
      slider.removeEventListener('pointerup', onPointerUp)
      slider.removeEventListener('pointercancel', onPointerUp)
      slider.removeEventListener('pointerleave', onPointerLeave)
      slider.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', onResize)
      element.remove()
      state = null
      ctx = null
    },
  }
}
